import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from 'antd';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import HomePage from './pages/HomePage';

import ProjectManagementPage from './pages/ProjectManagementPage';
import ScriptPage from './pages/ScriptPage';
import AssetsPage from './pages/AssetsPage';
import StoryboardPage from './pages/StoryboardPage';
import ProductionPage from './pages/ProductionPage';
import FinalReviewPage from './pages/FinalReviewPage';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/LoginPage';
import RechargePage from './pages/RechargePage';
import ProfilePage from './pages/ProfilePage';
import TransactionPage from './pages/TransactionPage';
import AdminPage from './pages/AdminPage';
import OrderDetailPage from './pages/OrderDetailPage';
import Navigation from './components/Navigation';

const { Header, Content } = Layout;

// 根据角色获取首页路径
const getHomePath = (role) => {
  if (role === 'admin') return '/admin';
  if (role === 'publisher') return '/';
  return '/';
};

// 受保护的路由组件
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return <div>加载中...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  const role = user?.role || 'creator';
  
  // 如果指定了允许的角色，检查当前用户角色是否在允许列表中
  if (allowedRoles && !allowedRoles.includes(role)) {
    return <Navigate to={getHomePath(role)} replace />;
  }
  
  return children;
};

// 公开路由（已登录用户重定向到首页）
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return <div>加载中...</div>;
  }
  
  if (isAuthenticated) {
    return <Navigate to={getHomePath(user?.role)} replace />;
  }
  
  return children;
};

const AppContent = () => {
  const { isAuthenticated } = useAuth();
  
  return (
    <Layout style={{ minHeight: '100vh', background: '#0d0d12' }}>
      {isAuthenticated && (
        <Header style={{ background: '#15151c', padding: 0, borderBottom: '1px solid #2a2a3a' }}>
          <Navigation />
        </Header>
      )}
      <Content style={{ 
        padding: isAuthenticated ? '24px' : 0, 
        background: '#0d0d12',
        minHeight: isAuthenticated ? 'calc(100vh - 64px)' : '100vh'
      }}>
        <Routes>
          {/* 公开路由 */}
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } 
          />
          
          {/* 受保护的路由 */}
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/project/:id" 
            element={
              <ProtectedRoute>
                <ProjectManagementPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/project/:id/script" 
            element={
              <ProtectedRoute>
                <ScriptPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/project/:id/assets" 
            element={
              <ProtectedRoute>
                <AssetsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/project/:id/storyboard" 
            element={
              <ProtectedRoute>
                <StoryboardPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/project/:id/production" 
            element={
              <ProtectedRoute>
                <ProductionPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/project/:id/final-review" 
            element={
              <ProtectedRoute>
                <FinalReviewPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/project/:id/settings" 
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/recharge"
            element={
              <ProtectedRoute>
                <RechargePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/transactions" 
            element={
              <ProtectedRoute>
                <TransactionPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/order/:id" 
            element={
              <ProtectedRoute>
                <OrderDetailPage />
              </ProtectedRoute>
            } 
          />
          
          {/* 默认重定向 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Content>
    </Layout>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <Router basename="/shortdrama">
        <AppContent />
      </Router>
    </AuthProvider>
  );
};

export default App;
